import React, { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import MagicButton from 'components/MagicButton/MagicButton';
import { useBulkImportMutation } from 'store/services/collectionList/wantList/wantListApi';
import { useBulkImportHaveListMutation } from 'store/services/collectionList/haveList/haveListApi';
import './ImportCardsModal.css';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCardAdd: (identifier: number | string, cardData?: any, searchMode?: 'any' | 'specific') => void;
  useOracleId?: boolean;
  isHaveList?: boolean;
}

interface ImportResult {
  totalCards: number;
  successCount: number;
  failedCount: number;
  failedCards: Array<{
    cardName: string;
    error: string;
  }>;
}

type InputMode = 'paste' | 'csv';

export default function ImportCardsModal({ isOpen, onClose, onCardAdd, useOracleId = false, isHaveList = false }: Props) {
  const [inputMode, setInputMode] = useState<InputMode>('paste');
  const [pasteText, setPasteText] = useState('');
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [progressMessage, setProgressMessage] = useState('Importing cards...');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const progressTimerRef = useRef<NodeJS.Timeout | null>(null);

  // RTK Query hooks
  const [wantListBulkImport] = useBulkImportMutation();
  const [haveListBulkImport] = useBulkImportHaveListMutation();
  const bulkImport = isHaveList ? haveListBulkImport : wantListBulkImport;

  // Timer to update progress message after 5 seconds
  useEffect(() => {
    if (isImporting) {
      // Reset message when import starts
      setProgressMessage('Importing cards...');

      // Set timer to change message after 5 seconds
      progressTimerRef.current = setTimeout(() => {
        setProgressMessage('This could take a few minutes for very large files.');
      }, 5000);
    } else {
      // Clear timer when import stops
      if (progressTimerRef.current) {
        clearTimeout(progressTimerRef.current);
        progressTimerRef.current = null;
      }
      // Reset message for next import
      setProgressMessage('Importing cards...');
    }

    // Cleanup on unmount
    return () => {
      if (progressTimerRef.current) {
        clearTimeout(progressTimerRef.current);
      }
    };
  }, [isImporting]);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
        if (file.size > 10 * 1024 * 1024) {
          toast.error('File size must be less than 10MB');
          return;
        }
        setCsvFile(file);
      } else {
        toast.error('Please upload a CSV file');
      }
    }
  }, []);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }
      setCsvFile(file);
    }
  }, []);

  const handleImport = async () => {
    setIsImporting(true);
    setImportResult(null);

    try {
      let response;

      if (inputMode === 'paste') {
        if (!pasteText.trim()) {
          toast.error('Please enter card names to import');
          setIsImporting(false);
          return;
        }

        // Send text directly to backend for parsing
        response = await bulkImport({
          text: pasteText
        }).unwrap();

      } else {
        if (!csvFile) {
          toast.error('Please select a CSV file to import');
          setIsImporting(false);
          return;
        }

        // Send CSV file directly to backend
        const formData = new FormData();
        formData.append('file', csvFile);

        response = await bulkImport(formData).unwrap();
      }

      // Process backend response
      const result: ImportResult = {
        totalCards: response.totalRows,
        successCount: response.successCount,
        failedCount: response.errorCount,
        failedCards: response.errors.map((err: any) => ({
          cardName: err.cardName || 'Unknown',
          error: err.error
        }))
      };

      setImportResult(result);

      if (result.successCount > 0) {
        toast.success(`Successfully imported ${result.successCount} card${result.successCount !== 1 ? 's' : ''} to your ${isHaveList ? 'Have' : 'Want'} List`);
      }

      if (result.failedCount > 0) {
        toast.warning(`Failed to import ${result.failedCount} card${result.failedCount !== 1 ? 's' : ''}`);
      }

    } catch (error: any) {
      console.error('Import error:', error);

      // Check if this is a timeout/network error (including CloudFront 504)
      const isTimeoutOrNetworkError =
        error?.name === 'AbortError' ||
        error?.status === 'TIMEOUT_ERROR' ||
        error?.status === 'FETCH_ERROR' ||  // CloudFront 504 Gateway Timeout
        error?.error?.includes?.('Failed to fetch') ||  // Network failure message
        error?.message?.includes?.('timeout') ||
        error?.message?.includes?.('aborted');

      if (isTimeoutOrNetworkError) {
        // Show informative message for timeout - import may still be processing
        toast.warning(
          'Import is taking longer than expected. Your cards may still be importing in the background. ' +
          'Please wait a few minutes and refresh the page to check your collection.',
          { autoClose: false }
        );
        // Set a special "pending" state instead of "0 Imported"
        setImportResult({
          totalCards: -1, // -1 indicates unknown/pending
          successCount: -1,
          failedCount: 0,
          failedCards: []
        });
      } else if (error?.data?.message) {
        // Check for specific error messages from backend
        toast.error(error.data.message);
        setImportResult({
          totalCards: 0,
          successCount: 0,
          failedCount: 0,
          failedCards: []
        });
      } else if (error?.data?.errors && Array.isArray(error.data.errors)) {
        // If backend returns validation errors
        toast.error(`Import failed: ${error.data.errors[0]?.error || 'Unknown error'}`);
        setImportResult({
          totalCards: 0,
          successCount: 0,
          failedCount: 0,
          failedCards: []
        });
      } else {
        toast.error('An error occurred during import. Please try again.');
        setImportResult({
          totalCards: 0,
          successCount: 0,
          failedCount: 0,
          failedCards: []
        });
      }
    } finally {
      setIsImporting(false);
    }
  };

  const handleReset = () => {
    setPasteText('');
    setCsvFile(null);
    setImportResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="import-modal-overlay" onClick={handleClose}>
      <div className="import-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="import-modal-header">
          <h2>Import Cards to {isHaveList ? 'Have List' : 'Want List'}</h2>
          <button className="import-modal-close" onClick={handleClose}>
            <FontAwesomeIcon icon="times" />
          </button>
        </div>

        <div className="import-modal-body">
          <div className="import-mode-selector">
            <label>Input Method:</label>
            <select
              value={inputMode}
              onChange={(e) => {
                setInputMode(e.target.value as InputMode);
                handleReset();
              }}
              disabled={isImporting}
            >
              <option value="paste">Copy/Paste</option>
              <option value="csv">Upload CSV</option>
            </select>
          </div>

          {inputMode === 'paste' ? (
            <div className="import-paste-section">
              <textarea
                className="import-paste-textarea"
                placeholder={isHaveList ? `Enter card names with sets, one per line:

2 Lightning Bolt [LEA]
Black Lotus [LEA]
4x Counterspell [LEA] NM

Set code is REQUIRED for Have List:
• Use [SET] after card name
• Example: Lightning Bolt [LEA]
• Defaults: Qty=1, Lang=EN, Cond=NM, Foil=false` : `Enter card names, one per line:

Lightning Bolt
4 Counterspell
2x Brainstorm

Supported formats:
• Card Name (defaults to 1)
• 4 Card Name
• 4x Card Name`}
                value={pasteText}
                onChange={(e) => setPasteText(e.target.value)}
                disabled={isImporting}
              />
            </div>
          ) : (
            <div
              className={`import-dropzone ${isDragging ? 'dragging' : ''}`}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
                disabled={isImporting}
              />

              {csvFile ? (
                <div className="import-file-info">
                  <FontAwesomeIcon icon="file-csv" size="2x" />
                  <p>{csvFile.name}</p>
                  <p className="import-file-size">
                    {(csvFile.size / 1024).toFixed(1)} KB
                  </p>
                  <button
                    className="import-file-remove"
                    onClick={() => {
                      setCsvFile(null);
                      if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                      }
                    }}
                    disabled={isImporting}
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <>
                  <FontAwesomeIcon icon="cloud-upload-alt" size="3x" />
                  <p>Drop CSV file here or</p>
                  <button
                    className="import-browse-btn"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isImporting}
                  >
                    Choose File
                  </button>
                  <p className="import-format-hint">
                    Max file size: 10MB • Max cards: 5,000<br />
                    Format: Card Name, Quantity, Set{isHaveList ? ' (required)' : ''}, Language, Condition, Foil
                  </p>
                </>
              )}
            </div>
          )}

          {isImporting && (
            <div className="import-progress-section">
              <div className="import-progress-bar">
                <div
                  className="import-progress-fill import-progress-indeterminate"
                />
              </div>
              <p className="import-progress-text">
                {progressMessage}
              </p>
            </div>
          )}

          {importResult && !isImporting && (
            <div className="import-result-section">
              {importResult.successCount === -1 ? (
                // Pending/timeout state - import may still be processing
                <div className="import-result-pending">
                  <FontAwesomeIcon icon="clock" />
                  <span>Import may still be processing...</span>
                  <p className="import-pending-hint">
                    Please refresh the page in a few minutes to check your collection.
                  </p>
                </div>
              ) : (
                // Normal result display
                <>
                  <div className="import-result-summary">
                    <div className="import-result-stat success">
                      <FontAwesomeIcon icon="check-circle" />
                      <span>{importResult.successCount} Imported</span>
                    </div>
                    {importResult.failedCount > 0 && (
                      <div className="import-result-stat failed">
                        <FontAwesomeIcon icon="times-circle" />
                        <span>{importResult.failedCount} Failed</span>
                      </div>
                    )}
                  </div>

                  {importResult.failedCards.length > 0 && (
                    <details className="import-failed-details">
                      <summary>View failed cards</summary>
                      <div className="import-failed-list">
                        {importResult.failedCards.map((card, index) => (
                          <div key={index} className="import-failed-item">
                            <span className="import-failed-name">{card.cardName}</span>
                            <span className="import-failed-error">{card.error}</span>
                          </div>
                        ))}
                      </div>
                    </details>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        <div className="import-modal-footer">
          <MagicButton
            label={importResult ? 'Done' : 'Cancel'}
            color="grey"
            onPress={handleClose}
            isDisabled={isImporting}
          />

          {!importResult && (
            <MagicButton
              label={isImporting ? 'Importing...' : 'Import'}
              color="purple"
              onPress={handleImport}
              isDisabled={isImporting || (inputMode === 'paste' ? !pasteText.trim() : !csvFile)}
              isLoading={isImporting}
            />
          )}

          {importResult && (
            <MagicButton
              label="Import More"
              color="purple"
              onPress={handleReset}
            />
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}